import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { loadTokenAsync, saveToken, clearToken, requestPersistentStorage } from "./tokenStore";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCompany, setActiveCompany] = useState(null);

  const refresh = useCallback(async () => {
    // Hydrate: on iOS PWA installs, localStorage may have been wiped
    // during the 7-day inactivity window but IndexedDB still holds the
    // token. loadTokenAsync() will restore it before we hit /auth/me.
    const token = await loadTokenAsync();
    if (!token) {
      setUser(null);
      setActiveCompany(null);
      setLoading(false);
      return;
    }
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      if (me.data.active_company_id) {
        const cs = await api.get("/companies");
        const c = cs.data.find((x) => x.id === me.data.active_company_id) || cs.data[0];
        setActiveCompany(c || null);
      }
    } catch (e) {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, totp_code) => {
    const r = await api.post("/auth/login", { email, password, totp_code });
    saveToken(r.data.access_token);
    // Fire a persistent-storage request explicitly: this is the moment
    // the user is most likely to accept it (they just proved they want
    // to stay logged in). The browser only prompts once per origin.
    requestPersistentStorage();
    await refresh();
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    saveToken(r.data.access_token);
    requestPersistentStorage();
    await refresh();
    return r.data.user;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setActiveCompany(null);
    window.location.href = "/";
  };

  const switchCompany = async (cid) => {
    await api.post(`/companies/${cid}/switch`);
    await refresh();
  };

  return (
    <AuthCtx.Provider
      value={{ user, activeCompany, loading, login, register, logout, refresh, switchCompany, setActiveCompany }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
